/**
 * Changelog `types` shared by every configulator project that releases.
 *
 * `commit-and-tag-version` defaults to the **conventionalcommits** preset,
 * whose stock type list marks `chore`, `docs`, `style`, `refactor`, `perf`
 * and `test` as `hidden` and has no entry at all for `build`, `ci` or
 * `revert`. Only `feat` and `fix` produce a visible section, so a release
 * whose range holds nothing else ships notes containing just the version
 * heading.
 *
 * That emptiness is not cosmetic. Hiding is a changelog-*writer* concern and
 * never reaches the bump decision: the preset's `whatBump` starts at patch
 * and only moves up for a `feat` or a breaking change — it has no "no bump"
 * outcome. The only thing that yields `bump: none` is `releasableCommits`
 * returning an empty list. A range of nothing but `chore:` commits therefore
 * publishes a patch release that cannot say why it exists.
 *
 * Listing every type with a section makes the notes account for the whole
 * range instead. It deliberately does not change any version number — the
 * bump ignores `types` entirely.
 */
export const CHANGELOG_TYPES: readonly ChangelogType[] = [
  { type: "feat", section: "Features" },
  { type: "fix", section: "Bug Fixes" },
  { type: "perf", section: "Performance Improvements" },
  { type: "revert", section: "Reverts" },
  { type: "refactor", section: "Code Refactoring" },
  { type: "docs", section: "Documentation" },
  { type: "test", section: "Tests" },
  { type: "build", section: "Build System" },
  { type: "ci", section: "Continuous Integration" },
  { type: "style", section: "Styles" },
  { type: "chore", section: "Miscellaneous Chores" },
];

/**
 * One entry in a changelog `types` list.
 */
export interface ChangelogType {
  /**
   * The conventional-commit type this entry matches, e.g. `"feat"`.
   */
  readonly type: string;

  /**
   * Heading the matching commits are grouped under. Section order in the
   * rendered notes follows the order of the list itself.
   */
  readonly section?: string;

  /**
   * Whether to omit matching commits from the notes. Omitted entirely by
   * `CHANGELOG_TYPES` — every type it lists is visible.
   */
  readonly hidden?: boolean;
}

/**
 * Resolve `versionrcOptions` for a project, given the already-merged options
 * and what the consumer actually passed.
 *
 * `ts-deepmerge` concatenates arrays rather than replacing them, so a
 * consumer-supplied `types` would arrive appended to `CHANGELOG_TYPES`. The
 * preset resolves a commit's entry with `types.find(...)`, so the default
 * entry would win every lookup and the consumer's list would be silently
 * inert. A supplied list replaces ours outright instead.
 *
 * Replacing is also the safer half of the trade: a consumer who passes a
 * partial list gets exactly that list, which is visibly wrong the first time
 * they look at their notes, rather than a silently ignored override.
 */
export function resolveVersionrcOptions(
  merged: Record<string, any> | undefined,
  userSupplied: Record<string, any> | undefined,
): Record<string, any> | undefined {
  if (!userSupplied?.types) {
    return merged;
  }

  return { ...merged, types: userSupplied.types };
}
